import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import RadioGroup from '@mui/material/RadioGroup';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import { useEffect, useState } from 'react';
import { useTravlData } from './TravlContext';
import { SurveyViewModel } from './ViewModels/SurveyViewModel';

const options = ['Arrivals', 'Departures', 'All Surveys'];

const extraFields = [
    "GFA", "RFA", "employ", "siteArea", "rooms", "seats", "beds",
    "doctors", "resUnits", "conRooms", "studentsA", "studentsB", "screens"
];

const staticFields = [
    "SurveyCode", "LandUse", "Name", "Borough", "SurveyDate", "PTAL", "Area", "TotalParking", "SurveyHrs1"
];

const timeFields = [
    "t_0000_0030", "t_0030_0100", "t_0100_0130", "t_0130_0200", "t_0200_0230", "t_0230_0300", "t_0300_0330", "t_0330_0400", "t_0400_0430", "t_0430_0500",
    "t_0500_0530", "t_0530_0600", "t_0600_0630", "t_0630_0700", "t_0700_0730", "t_0730_0800", "t_0800_0830", "t_0830_0900", "t_0900_0930", "t_0930_1000",
    "t_1000_1030", "t_1030_1100", "t_1100_1130", "t_1130_1200", "t_1200_1230", "t_1230_1300", "t_1300_1330", "t_1330_1400", "t_1400_1430", "t_1430_1500",
    "t_1500_1530", "t_1530_1600", "t_1600_1630", "t_1630_1700", "t_1700_1730", "t_1730_1800", "t_1800_1830", "t_1830_1900", "t_1900_1930", "t_1930_2000",
    "t_2000_2030", "t_2030_2100", "t_2100_2130", "t_2130_2200", "t_2200_2230", "t_2230_2300", "t_2300_2330", "t_2330_2400"
];

const modeCodeMap: Record<string, string> = {
    ALL: "All Modes",
    BUS: "Bus",
    CAR: "Car",
    CARA: "All Car Drivers",
    CARD: "Car Driver (alone)",
    CARM: "Car Driver (with pass)",
    CARP: "Car Passenger",
    COAC: "Coach",
    DLR: "DLR",
    HGV: "HGV",
    MOTC: "Motor Cycle",
    OTHR: "Other",
    PARB: "Park and Ride Bus",
    PEDC: "Pedal Cycle",
    RAIL: "Rail",
    RBOT: "River Boat",
    TAXI: "Taxi",
    TRAM: "Tram",
    TUBE: "Underground",
    UNKW: "Unknown",
    WALK: "Walk/PT",
    WALO: "Walk"
};

const getActiveExtraFields = (dataToExport: any[]) => {
    return extraFields.filter(key =>
        dataToExport.some(row => !!row[key] && row[key] !== "0")
    );
};

interface IProps {
    open: boolean;
    onClose: () => void;
    landUse: string;
}

export default function DirectionChoiceDialog(props: IProps) {
    const { open, onClose, landUse } = props;
    const { siteData, surveyData, setFilterSurveyCodes } = useTravlData();

    const [direction, setDirection] = useState('Arrivals');

    useEffect(() => {
        var surveyCodes = siteData.filter(f => f.LandUseDesc === landUse).map(p => p.SurveyCode);
        setFilterSurveyCodes(surveyCodes);
    }, [landUse]);

    const exportCsv = () => {
        const validSurveyData = surveyData.filter(e => !!e.SurveyCode && !!e.ModeCode);

        const timeBandToField = (timeBand: string): string | undefined => {
            const match = timeBand.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
            if (!match) return undefined;
            const [, h1, m1, h2, m2] = match;
            const from = h1.padStart(2, '0') + m1;
            const to = h2.padStart(2, '0') + m2;
            return `t_${ from }_${ to }`;
        };

        const grouped: Record<string, SurveyViewModel[]> = {};

        for (const entry of validSurveyData) {
            const key = `${ entry.SurveyCode }__${ entry.ModeCode }`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(entry);
        }

        const rowsToExport = Object.entries(grouped).map(([key, entries]) => {
            const [surveyCode, modeCode] = key.split("__");
            const site = siteData.find(s => s.SurveyCode === surveyCode && s.LandUseDesc === landUse);
            if (!site) return null;

            const timeValues: Record<string, string> = {};
            timeFields.forEach(t => timeValues[t] = "");

            for (const entry of entries) {
                const field = timeBandToField(entry.TimeBand);
                if (field && timeFields.includes(field)) {
                    const val = parseInt(entry.JnysIn || "0", 10);

                    timeValues[field] = String(val);

                }
            }

            const activeExtras = getActiveExtraFields([site]);

            return {
                ...staticFields.reduce((obj, key) => ({ ...obj, [key]: site[key] ?? "" }), {}),
                ...activeExtras.reduce((obj, key) => ({ ...obj, [key]: site[key] ?? "" }), {}),
                Surveyed: modeCodeMap[modeCode] || modeCode,
                ...timeValues
            };
        }).filter(Boolean);

        if (!rowsToExport.length) {
            onClose();
            return;
        }

        rowsToExport.sort((a, b) => {
            const nameA = (a as Record<string, string>).Name?.toUpperCase() || "";
            const nameB = (b as Record<string, string>).Name?.toUpperCase() || "";
            return nameA.localeCompare(nameB);
        });

        const activeExtraFields = getActiveExtraFields(siteData);
        const headers = [
            ...staticFields,
            ...activeExtraFields,
            "Surveyed",
            ...timeFields
        ];

        const rows = [
            headers.join(","),
            ...rowsToExport.map(obj =>
                headers.map(h => {
                    const val = (obj as Record<string, string>)[h] ?? "";
                    const safeVal = String(val).replace(/"/g, '""');
                    return `${safeVal}`;
                }).join(",")
            )
        ];

        const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "exported_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Extract Data Options</DialogTitle>
            <DialogContent>
                <FormControl>
                    <RadioGroup
                        value={direction}
                        onChange={e => setDirection(e.target.value)}
                    >
                        {options.map((option, index) => (
                            <FormControlLabel
                                key={index}
                                value={option}
                                control={<Radio size="small" />}
                                label={option}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
                <DialogActions>
                    <Button onClick={exportCsv}>Ok</Button>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
